/**
 * Catalogo de insignias (seccion 9.2).
 *
 * Este fichero es la fuente de verdad del catalogo: de aqui salen el icono, el
 * tono, la dificultad y el valor que se ensenan en la vitrina, y de aqui se
 * genero la migracion que las siembra en la base (20260101002100). El
 * administrador puede crear insignias nuevas y otorgarlas a mano (RF-INS-04)
 * desde el panel.
 *
 * Como esta pensado el catalogo
 * -----------------------------
 * Hay insignias que se consiguen el primer dia y otras que cuestan temporadas.
 * Eso es deliberado: una vitrina donde todo esta a cero no motiva a nadie, y
 * una donde todo se consigue en una tarde tampoco. Por eso cada insignia
 * declara dos numeros:
 *
 *   - `difficulty` (1 a 5): cuanto cuesta conseguirla.
 *   - `value`: lo que pesa en la vitrina. No da puntos de ranking; es la vara
 *     para ordenar «de mas valiosa a menos» y para que una racha de veinte
 *     quincenas no valga lo mismo que un primer comentario.
 *
 * Los dos viajan a la base de datos, de modo que una insignia creada a mano
 * desde el panel tambien se pueda ordenar.
 */

export type BadgeCriteriaType =
  // --- Podio y clasificacion -------------------------------------------------
  | 'COMPETITION_FIRST'
  | 'COMPETITION_SECOND'
  | 'COMPETITION_THIRD'
  | 'WINS_TOTAL'
  | 'PODIUMS_TOTAL'
  | 'SEASON_CATEGORY_FIRST'
  | 'CITY_FIRST'
  | 'SEASON_OVERALL_FIRST'
  | 'GLOBAL_TOP3'
  | 'GLOBAL_TOP10'
  /**
   * Nº 1 del ranking global, ahora mismo.
   *
   * Es la unica insignia del catalogo que NO se gana para siempre: la
   * lleva una sola persona en cada momento y cambia de dueno en cuanto
   * alguien la supera. Todas las demas se conceden y se quedan.
   */
  | 'GLOBAL_FIRST'
  // --- Participacion ---------------------------------------------------------
  | 'FIRST_APPROVED'
  | 'APPROVED_MARKS'
  | 'ENROLLED_COMPETITIONS'
  | 'PROFILE_COMPLETE'
  // --- Constancia ------------------------------------------------------------
  | 'STREAK_FULL_FORTNIGHTS'
  | 'STREAK_FORTNIGHTS'
  | 'SEASONS_COMPLETED'
  // --- Puntos y rango --------------------------------------------------------
  | 'SEASON_POINTS'
  | 'RANK_REACHED'
  // --- Tematicas -------------------------------------------------------------
  | 'STATIC_WINS'
  | 'WEIGHTED_REPS_WIN'
  | 'ENDURANCE_WIN'
  | 'THEME_MARKS_DYNAMIC'
  | 'THEME_MARKS_STATIC'
  | 'THEME_MARKS_ENDURANCE'
  | 'THEME_MARKS_WEIGHTED'
  | 'ALL_THEMES'
  | 'CATEGORY_RECORD'
  // --- Comunidad -------------------------------------------------------------
  | 'COMMENTS_WRITTEN'
  | 'FORUM_POSTS'
  | 'REACTIONS_GIVEN'
  | 'REACTIONS_RECEIVED'
  // --- Momentos --------------------------------------------------------------
  | 'EARLY_SUBMISSION'
  | 'LAST_MINUTE_SUBMISSION'
  | 'NIGHT_SUBMISSION';

export type BadgeGlyph =
  | 'laurel'
  | 'shield-silver'
  | 'shield-bronze'
  | 'lambda'
  | 'tower'
  | 'crown'
  | 'spear'
  | 'wall'
  | 'column'
  | 'weight'
  | 'lungs'
  | 'blood'
  | 'hammer'
  | 'veteran'
  | 'peak'
  | 'tally'
  | 'chat'
  | 'board'
  | 'spark'
  | 'chevron'
  | 'ledger'
  | 'sun'
  | 'moon'
  | 'stopwatch'
  | 'rings'
  | 'podium'
  | 'kit'
  | 'calendar'
  | 'bar';

export interface BadgeSeed {
  code: string;
  criteriaType: BadgeCriteriaType;
  /** Umbral del criterio cuando aplica (rachas, numero de victorias...). */
  threshold?: number;
  /** Simbolo del icono generado por BadgeIcon. */
  glyph: BadgeGlyph;
  tone: 'gold' | 'silver' | 'bronze' | 'crimson';
  /** 1 = se consigue el primer dia; 5 = cuesta temporadas. */
  difficulty: 1 | 2 | 3 | 4 | 5;
  /** Lo que pesa en la vitrina. No son puntos de ranking. */
  value: number;
  /**
   * Solo una persona puede llevarla a la vez. Al concederla se retira a
   * quien la tuviese.
   */
  unique?: boolean;
}

export const BADGE_CATALOG: BadgeSeed[] = [
  /**
   * La Cima. La insignia exclusiva de la aplicacion.
   *
   * Va primera en el catalogo a proposito: es la que se ensena arriba del
   * todo en la vitrina. Le da nombre el propio producto —CaliPeak— y la lleva
   * quien encabeza el ranking global, uno solo, mientras lo encabece.
   */
  { code: 'the_summit', criteriaType: 'GLOBAL_FIRST', glyph: 'peak', tone: 'gold', difficulty: 5, value: 200, unique: true },

  // --- Podio -----------------------------------------------------------------
  { code: 'laurel_crown', criteriaType: 'COMPETITION_FIRST', glyph: 'laurel', tone: 'gold', difficulty: 4, value: 60 },
  { code: 'silver_shield', criteriaType: 'COMPETITION_SECOND', glyph: 'shield-silver', tone: 'silver', difficulty: 3, value: 40 },
  { code: 'bronze_shield', criteriaType: 'COMPETITION_THIRD', glyph: 'shield-bronze', tone: 'bronze', difficulty: 3, value: 30 },
  { code: 'podium_regular', criteriaType: 'PODIUMS_TOTAL', threshold: 5, glyph: 'podium', tone: 'silver', difficulty: 4, value: 70 },
  { code: 'triple_crown', criteriaType: 'WINS_TOTAL', threshold: 3, glyph: 'laurel', tone: 'gold', difficulty: 4, value: 90 },
  { code: 'conqueror', criteriaType: 'WINS_TOTAL', threshold: 10, glyph: 'crown', tone: 'gold', difficulty: 5, value: 170 },

  // --- Clasificacion ---------------------------------------------------------
  { code: 'golden_lambda', criteriaType: 'SEASON_CATEGORY_FIRST', glyph: 'lambda', tone: 'gold', difficulty: 5, value: 110 },
  { code: 'local_guardian', criteriaType: 'CITY_FIRST', glyph: 'tower', tone: 'bronze', difficulty: 3, value: 35 },
  { code: 'quarter_king', criteriaType: 'SEASON_OVERALL_FIRST', glyph: 'crown', tone: 'gold', difficulty: 5, value: 150 },
  { code: 'top_ten', criteriaType: 'GLOBAL_TOP10', glyph: 'chevron', tone: 'silver', difficulty: 4, value: 70 },
  { code: 'top_three', criteriaType: 'GLOBAL_TOP3', glyph: 'chevron', tone: 'gold', difficulty: 5, value: 120 },

  // --- Participacion: lo que se consigue entrenando y enviando ---------------
  { code: 'first_step', criteriaType: 'ENROLLED_COMPETITIONS', threshold: 1, glyph: 'bar', tone: 'bronze', difficulty: 1, value: 5 },
  { code: 'full_kit', criteriaType: 'PROFILE_COMPLETE', glyph: 'kit', tone: 'bronze', difficulty: 1, value: 5 },
  { code: 'first_blood', criteriaType: 'FIRST_APPROVED', glyph: 'blood', tone: 'crimson', difficulty: 1, value: 10 },
  { code: 'marks_5', criteriaType: 'APPROVED_MARKS', threshold: 5, glyph: 'tally', tone: 'bronze', difficulty: 2, value: 20 },
  { code: 'marks_10', criteriaType: 'APPROVED_MARKS', threshold: 10, glyph: 'tally', tone: 'silver', difficulty: 2, value: 30 },
  { code: 'marks_25', criteriaType: 'APPROVED_MARKS', threshold: 25, glyph: 'tally', tone: 'silver', difficulty: 3, value: 55 },
  { code: 'marks_50', criteriaType: 'APPROVED_MARKS', threshold: 50, glyph: 'tally', tone: 'gold', difficulty: 4, value: 95 },
  { code: 'marks_100', criteriaType: 'APPROVED_MARKS', threshold: 100, glyph: 'tally', tone: 'gold', difficulty: 5, value: 160 },

  // --- Constancia ------------------------------------------------------------
  { code: 'two_in_a_row', criteriaType: 'STREAK_FORTNIGHTS', threshold: 2, glyph: 'calendar', tone: 'bronze', difficulty: 1, value: 15 },
  { code: 'iron_guard', criteriaType: 'STREAK_FULL_FORTNIGHTS', threshold: 2, glyph: 'spear', tone: 'crimson', difficulty: 3, value: 40 },
  { code: 'spartan_wall', criteriaType: 'STREAK_FORTNIGHTS', threshold: 5, glyph: 'wall', tone: 'crimson', difficulty: 3, value: 50 },
  { code: 'ten_in_a_row', criteriaType: 'STREAK_FORTNIGHTS', threshold: 10, glyph: 'calendar', tone: 'gold', difficulty: 4, value: 100 },
  { code: 'relentless', criteriaType: 'STREAK_FORTNIGHTS', threshold: 20, glyph: 'calendar', tone: 'gold', difficulty: 5, value: 180 },
  { code: 'rookie_season', criteriaType: 'SEASONS_COMPLETED', threshold: 1, glyph: 'veteran', tone: 'bronze', difficulty: 2, value: 25 },
  { code: 'veteran', criteriaType: 'SEASONS_COMPLETED', threshold: 3, glyph: 'veteran', tone: 'silver', difficulty: 4, value: 80 },
  { code: 'old_guard', criteriaType: 'SEASONS_COMPLETED', threshold: 6, glyph: 'veteran', tone: 'gold', difficulty: 5, value: 150 },

  // --- Puntos y rango --------------------------------------------------------
  { code: 'first_hundred', criteriaType: 'SEASON_POINTS', threshold: 100, glyph: 'ledger', tone: 'bronze', difficulty: 1, value: 15 },
  { code: 'five_hundred', criteriaType: 'SEASON_POINTS', threshold: 500, glyph: 'ledger', tone: 'silver', difficulty: 2, value: 35 },
  { code: 'thousand_club', criteriaType: 'SEASON_POINTS', threshold: 1000, glyph: 'ledger', tone: 'gold', difficulty: 3, value: 65 },
  { code: 'iron_ledger', criteriaType: 'SEASON_POINTS', threshold: 2500, glyph: 'ledger', tone: 'gold', difficulty: 5, value: 130 },
  { code: 'rank_three', criteriaType: 'RANK_REACHED', threshold: 3, glyph: 'chevron', tone: 'bronze', difficulty: 2, value: 25 },
  { code: 'rank_five', criteriaType: 'RANK_REACHED', threshold: 5, glyph: 'chevron', tone: 'silver', difficulty: 3, value: 60 },
  { code: 'rank_seven', criteriaType: 'RANK_REACHED', threshold: 7, glyph: 'chevron', tone: 'gold', difficulty: 5, value: 140 },

  // --- Tematicas -------------------------------------------------------------
  { code: 'dynamo', criteriaType: 'THEME_MARKS_DYNAMIC', threshold: 5, glyph: 'bar', tone: 'silver', difficulty: 2, value: 30 },
  { code: 'statue', criteriaType: 'THEME_MARKS_STATIC', threshold: 5, glyph: 'column', tone: 'silver', difficulty: 2, value: 30 },
  { code: 'engine', criteriaType: 'THEME_MARKS_ENDURANCE', threshold: 5, glyph: 'lungs', tone: 'silver', difficulty: 2, value: 30 },
  { code: 'anvil', criteriaType: 'THEME_MARKS_WEIGHTED', threshold: 5, glyph: 'weight', tone: 'silver', difficulty: 2, value: 30 },
  { code: 'all_round', criteriaType: 'ALL_THEMES', glyph: 'rings', tone: 'gold', difficulty: 3, value: 55 },
  { code: 'isometric', criteriaType: 'STATIC_WINS', threshold: 3, glyph: 'column', tone: 'silver', difficulty: 4, value: 65 },
  { code: 'brute_force', criteriaType: 'WEIGHTED_REPS_WIN', glyph: 'weight', tone: 'bronze', difficulty: 3, value: 35 },
  { code: 'endless_breath', criteriaType: 'ENDURANCE_WIN', glyph: 'lungs', tone: 'silver', difficulty: 3, value: 35 },
  { code: 'record_breaker', criteriaType: 'CATEGORY_RECORD', glyph: 'hammer', tone: 'gold', difficulty: 5, value: 100 },

  // --- Comunidad -------------------------------------------------------------
  { code: 'first_word', criteriaType: 'COMMENTS_WRITTEN', threshold: 1, glyph: 'chat', tone: 'bronze', difficulty: 1, value: 5 },
  { code: 'good_crowd', criteriaType: 'COMMENTS_WRITTEN', threshold: 25, glyph: 'chat', tone: 'silver', difficulty: 2, value: 25 },
  { code: 'board_opener', criteriaType: 'FORUM_POSTS', threshold: 1, glyph: 'board', tone: 'bronze', difficulty: 1, value: 5 },
  { code: 'forum_voice', criteriaType: 'FORUM_POSTS', threshold: 25, glyph: 'board', tone: 'silver', difficulty: 3, value: 40 },
  { code: 'hype_man', criteriaType: 'REACTIONS_GIVEN', threshold: 25, glyph: 'spark', tone: 'bronze', difficulty: 2, value: 20 },
  { code: 'crowd_favourite', criteriaType: 'REACTIONS_RECEIVED', threshold: 50, glyph: 'spark', tone: 'silver', difficulty: 3, value: 45 },
  { code: 'arena_idol', criteriaType: 'REACTIONS_RECEIVED', threshold: 200, glyph: 'spark', tone: 'gold', difficulty: 4, value: 90 },

  // --- Momentos: las que cuentan una historia --------------------------------
  { code: 'early_bird', criteriaType: 'EARLY_SUBMISSION', glyph: 'sun', tone: 'bronze', difficulty: 2, value: 20 },
  { code: 'buzzer_beater', criteriaType: 'LAST_MINUTE_SUBMISSION', glyph: 'stopwatch', tone: 'bronze', difficulty: 2, value: 20 },
  { code: 'night_shift', criteriaType: 'NIGHT_SUBMISSION', glyph: 'moon', tone: 'bronze', difficulty: 2, value: 20 },
];

/** Numero maximo de insignias destacadas junto al avatar (RF-INS-02). */
export const MAX_FEATURED_BADGES = 3;

/** Dificultad maxima, para pintar la escala sin numeros magicos. */
export const MAX_BADGE_DIFFICULTY = 5;

/** Lo que supone la vitrina de una insignia que no este en este catalogo. */
export const DEFAULT_BADGE_DIFFICULTY = 3;
export const DEFAULT_BADGE_VALUE = 25;
